import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Plus, Search, Trash2 } from 'lucide-react';
import Loader from '../Loader';
import { Badge, Confirm, Empty, HScroll, useWheelToPage } from './UpUi';
import { clock, date, dateTime, duration, percent } from './format';

const Title = ({ content, episode }) => (
    <span className='up-title'>
        {content?.poster ? <img src={content.poster} alt='' loading='lazy' /> : <span className='up-poster-ph'></span>}
        <span className='up-title-text'>
            <b className='truncate' title={content?.title}>{content?.title || '—'}</b>
            <small>
                {episode ? `${episode.label}${episode.title ? ` · ${episode.title}` : ''}` : content?.type === 'SERIE' ? 'Série' : content?.type === 'FILM' ? 'Film' : ''}
                {content?.year ? ` · ${content.year}` : ''}
            </small>
        </span>
    </span>
)

const Bar = ({ value }) => (
    <span className='up-bar' title={`${value} %`}><span style={{ width: `${value}%` }}></span></span>
)

/** Ajout d'un titre à la liste : recherche dans le catalogue du projet. */
const AddToList = ({ pApi, onAdd }) => {
    const [q, setQ] = useState('')
    const [items, setItems] = useState([])
    useEffect(() => {
        if (q.trim().length < 2) return setItems([])
        const t = setTimeout(async () => {
            try { setItems((await pApi.get('/contents', { q })).items) } catch { setItems([]) }
        }, 300)
        return () => clearTimeout(t)
    }, [q, pApi])
    return (
        <div className='up-add'>
            <div className='up-search'>
                <Search size={15} />
                <input placeholder='Ajouter un titre à sa liste…' value={q} onChange={(e) => setQ(e.currentTarget.value)} />
            </div>
            {items.length > 0 && (
                <div className='up-add-results'>
                    {items.map((c) => (
                        <button key={c.id} onClick={() => { onAdd(c); setQ('') }}>
                            <Title content={c} /> <Plus size={15} />
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}

const TABS = [
    ['history', 'Historique'],
    ['progress', 'En cours'],
    ['list', 'Ma liste'],
    ['ratings', 'Notes'],
]

/** Données d'un profil (GET /users/:id/profiles/:pid) : tout se voit, tout se retire. */
const UpProfileData = ({ pApi, userId, profileId, notify, onChanged }) => {
    const [data, setData] = useState(null)
    const [error, setError] = useState('')
    const [tab, setTab] = useState('history')
    const [confirm, setConfirm] = useState(null)
    const tabsRef = useWheelToPage()
    const base = `/users/${userId}/profiles/${profileId}`

    const load = useCallback(async () => {
        setError('')
        try { setData(await pApi.get(base)) } catch (e) { setError(e.message) }
    }, [pApi, base])
    useEffect(() => { load() }, [load])

    const remove = (title, path, done) => setConfirm({
        title,
        text: 'Effacé tout de suite chez le user (base et copie de l’app).',
        action: 'Effacer',
        danger: true,
        onConfirm: async () => {
            try { await pApi.del(path); notify(done); load(); onChanged() } catch (e) { notify(e.message, true) }
        },
    })
    const add = async (c) => {
        try { await pApi.post(`${base}/list`, { contentId: c.id }); notify(`${c.title} ajouté à sa liste`); load(); onChanged() } catch (e) { notify(e.message, true) }
    }

    if (error) return <p className='up-error'>{error}</p>
    if (!data) return <div className='up-loading'><Loader /></div>
    const count = { history: data.history.length, progress: data.progress.length, list: data.list.length, ratings: data.ratings.length }

    return (
        <div className='up-pdata'>
            <nav className='up-tabs small sub' ref={tabsRef}>
                {TABS.map(([id, label]) => (
                    <button key={id} className={tab === id ? 'on' : ''} onClick={() => setTab(id)}>{label} <span className='up-tab-count muted'>{count[id]}</span></button>
                ))}
                {tab === 'history' && data.history.length > 0 && (
                    <button className='up-tab-action' onClick={() => remove('Effacer tout l’historique', `${base}/history`, 'Historique effacé')}><Trash2 size={13} /> Tout effacer</button>
                )}
            </nav>

            {tab === 'history' && (
                <HScroll>
                    <div className='up-table up-table-history'>
                        <div className='up-thead'>
                            <span>Vidéo</span><span>Commencé</span><span>Dernière fois</span><span className='tac'>Regardé</span><span>Avancement</span><span>Fini</span><span></span>
                        </div>
                        {data.history.map((h) => {
                            const pct = percent(h.maxPosition, h.duration)
                            return (
                                <div key={`${h.contentId}-${h.episodeId}`} className='up-row static'>
                                    <Title content={h.content} episode={h.episode} />
                                    <span>{dateTime(h.startedAt)}</span>
                                    <span>{dateTime(h.lastAt)}</span>
                                    <span className='tac' title='Temps vraiment regardé (sauts non comptés)'><b>{duration(h.watchedSeconds)}</b><small className='up-muted'> / {duration(h.duration)}</small></span>
                                    <span className='up-progress'><Bar value={pct} /> <small>{pct} %</small></span>
                                    <span>
                                        {h.finishedAt ? (
                                            <span className='up-finished' title={`fini le ${dateTime(h.finishedAt)}`}>
                                                <CheckCircle2 size={14} /> en {duration((new Date(h.finishedAt) - new Date(h.startedAt)) / 1000)}
                                            </span>
                                        ) : <span className='up-muted'>—</span>}
                                    </span>
                                    <span className='tar'>
                                        <button className='pay-act-btn' title='Effacer de l’historique' onClick={() => remove('Effacer de l’historique', `${base}/history?contentId=${h.contentId}${h.episodeId ? `&episodeId=${h.episodeId}` : ''}`, 'Effacé de l’historique')}>
                                            <Trash2 size={15} color='#f71717' />
                                        </button>
                                    </span>
                                </div>
                            )
                        })}
                        {!data.history.length && <Empty>Rien regardé pour l'instant</Empty>}
                    </div>
                </HScroll>
            )}

            {tab === 'progress' && (
                <HScroll>
                    <div className='up-table up-table-progress'>
                        <div className='up-thead'><span>Vidéo</span><span>Position</span><span>Mis à jour</span><span></span></div>
                        {data.progress.map((p) => {
                            const pct = percent(p.position, p.duration)
                            return (
                                <div key={p.content.id} className='up-row static'>
                                    <Title content={p.content} episode={p.episode} />
                                    <span className='up-progress'><Bar value={pct} /> <small>{clock(p.position)} / {clock(p.duration)}</small></span>
                                    <span>{dateTime(p.updatedAt)}</span>
                                    <span className='tar'>
                                        <button className='pay-act-btn' title='Retirer (il repart de zéro)' onClick={() => remove('Retirer la progression', `${base}/progress/${p.content.id}`, 'Progression retirée')}>
                                            <Trash2 size={15} color='#f71717' />
                                        </button>
                                    </span>
                                </div>
                            )
                        })}
                        {!data.progress.length && <Empty>Rien en cours</Empty>}
                    </div>
                </HScroll>
            )}

            {tab === 'list' && (
                <>
                    <AddToList pApi={pApi} onAdd={add} />
                    <div className='up-table up-table-list'>
                        <div className='up-thead'><span>Titre</span><span>Ajouté le</span><span></span></div>
                        {data.list.map((x) => (
                            <div key={x.content.id} className='up-row static'>
                                <Title content={x.content} />
                                <span>{date(x.addedAt)}</span>
                                <span className='tar'>
                                    <button className='pay-act-btn' title='Retirer de sa liste' onClick={() => remove('Retirer de sa liste', `${base}/list/${x.content.id}`, 'Retiré de sa liste')}>
                                        <Trash2 size={15} color='#f71717' />
                                    </button>
                                </span>
                            </div>
                        ))}
                        {!data.list.length && <Empty>Liste vide</Empty>}
                    </div>
                </>
            )}

            {tab === 'ratings' && (
                <div className='up-table up-table-ratings'>
                    <div className='up-thead'><span>Titre</span><span className='tac'>Note</span><span>Commentaire</span><span>Date</span><span></span></div>
                    {data.ratings.map((r) => (
                        <div key={r.id} className='up-row static'>
                            <Title content={r.content} />
                            <span className='tac'><Badge tone={r.value >= 70 ? 'ok' : r.value >= 40 ? 'warn' : 'ko'}>{r.value} %</Badge></span>
                            <span className='up-comment'>{r.comment || <span className='up-muted'>—</span>}{r.anonymous && <Badge>anonyme</Badge>}</span>
                            <span>{date(r.updatedAt)}</span>
                            <span className='tar'>
                                <button className='pay-act-btn' title='Supprimer la note' onClick={() => remove('Supprimer la note', `${base}/ratings/${r.content.id}`, 'Note supprimée')}>
                                    <Trash2 size={15} color='#f71717' />
                                </button>
                            </span>
                        </div>
                    ))}
                    {!data.ratings.length && <Empty>Aucune note</Empty>}
                </div>
            )}

            {confirm && <Confirm {...confirm} onClose={() => setConfirm(null)} />}
        </div>
    )
}

export default UpProfileData;
